import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../ui/card";
import { Progress } from "../ui/progress";
import { Avatar } from "../badges";

export default function OverviewTab({ project, tasks, requirements, issues, users }) {
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    api.get("/activities", { params: { project_id: project.id, limit: 15 } }).then((r) => setActivities(r.data));
  }, [project.id]);

  const done = tasks.filter((t) => t.status === "completed").length;
  const progress = tasks.length ? Math.round((done / tasks.length) * 100) : 0;
  const byStatus = (s) => tasks.filter((t) => t.status === s).length;
  const members = users.filter((u) => (project.member_ids || []).includes(u.id));

  const stats = [
    { label: "Progress", value: `${progress}%` },
    { label: "In Progress", value: byStatus("in_progress") },
    { label: "Blocked", value: byStatus("blocked") },
    { label: "Open Issues", value: issues.filter((i) => i.status === "open").length },
    { label: "Requirements", value: requirements.length },
  ];

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="overview-tab-content">
      <div className="lg:col-span-2 space-y-6">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 stagger">
          {stats.map((s) => (
            <Card key={s.label} className="p-4">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{s.label}</p>
              <p className="font-heading text-2xl font-bold mt-1">{s.value}</p>
            </Card>
          ))}
        </div>
        <Card className="p-5">
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Overall completion</p>
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-muted-foreground mt-2">{done} of {tasks.length} tasks completed</p>
        </Card>
        <div>
          <h3 className="font-heading font-semibold mb-3">Recent Activity</h3>
          <Card className="divide-y divide-border">
            {activities.map((a) => (
              <div key={a.id} className="p-3.5 flex items-start gap-3 text-sm" data-testid="activity-item">
                <Avatar name={a.actor_name} size="xs" />
                <div className="min-w-0">
                  <p>
                    <span className="font-medium">{a.actor_name}</span>{" "}
                    <span className="text-muted-foreground">{a.action}</span>{" "}
                    <span className="font-mono text-xs text-primary">{a.entity_ref}</span>
                  </p>
                  {a.detail && <p className="text-xs text-muted-foreground truncate">{a.detail}</p>}
                  <p className="text-[10px] font-mono text-muted-foreground mt-0.5">{new Date(a.created_at).toLocaleString()}</p>
                </div>
              </div>
            ))}
            {!activities.length && <p className="p-4 text-sm text-muted-foreground">No activity yet.</p>}
          </Card>
        </div>
      </div>
      <div>
        <h3 className="font-heading font-semibold mb-3">Team</h3>
        <Card className="divide-y divide-border">
          {members.map((m) => (
            <div key={m.id} className="p-3.5 flex items-center gap-3">
              <Avatar name={m.name} size="md" />
              <div>
                <p className="text-sm font-medium">{m.name}</p>
                <p className="text-[10px] font-mono uppercase text-muted-foreground">{m.role}</p>
              </div>
            </div>
          ))}
          {!members.length && <p className="p-4 text-sm text-muted-foreground">No members assigned.</p>}
        </Card>
      </div>
    </div>
  );
}
